import { cn } from '@Pages/pageRuntime/lib/utils';
import Icon from 'lucide-react/dist/esm/Icon';
import Circle from 'lucide-react/dist/esm/icons/circle';
import { useEffect, useSyncExternalStore } from 'react';

import { loadIconNode, readIconNode, subscribeToIconNodes } from './dynamicIconNodes';
import { lucideIconNameToKebabCase } from './lucideIconNames';

interface SafeDynamicIconProps {
  name?: string | null;
  className?: string;
  size?: number;
  strokeWidth?: number;
  color?: string;
}

/**
 * Renders a Lucide icon by name.
 *
 * An icon whose shape is already known renders at once. Any other icon keeps
 * an empty box of its final size until its shape loads, so nothing changes
 * shape or moves when it arrives. A circle stands in only for a missing name
 * and for a name Lucide does not have.
 */
export function SafeDynamicIcon({
  name,
  className,
  size,
  strokeWidth,
  color
}: SafeDynamicIconProps) {
  const key = name ? lucideIconNameToKebabCase(name) : null;
  const node = useSyncExternalStore(subscribeToIconNodes, () =>
    key === null ? null : readIconNode(key)
  );

  useEffect(() => {
    if (key !== null && node === undefined) {
      void loadIconNode(key);
    }
  }, [key, node]);

  if (node === null) {
    return <Circle className={className} size={size} strokeWidth={strokeWidth} color={color} />;
  }

  if (node === undefined) {
    return (
      <svg
        aria-hidden="true"
        width={size ?? 24}
        height={size ?? 24}
        className={cn('lucide', `lucide-${key}`, className)}
      />
    );
  }

  return (
    <Icon
      iconNode={node}
      className={cn(`lucide-${key}`, className)}
      size={size}
      strokeWidth={strokeWidth}
      color={color}
    />
  );
}
